/**
 * PublicationsTab — IN REVIEW stage
 * Publication summary: stats grid + publication list with links
 */

import type { ProjectDetailProject, ProjectInteraction } from '../../../types'
import { CompletableSection } from '../../ui/CompletableSection'
import type { SectionCompletionState } from '../../../hooks/useSectionCompletions'

export interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}

export function PublicationsTab({
  project,
  projectId,
  domain,
  onFieldChange,
  sectionCompletions: sc,
  tabId = 'tab-publications',
}: TabContentProps) {
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId, sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId, sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId, sectionId)

  const publications = project.publications || []
  const interactions = project.interactions || []

  // Calculate stats
  const totalPublications = publications.length
  const lastThreeYears = publications.filter((p) => {
    const year = new Date(p.published_at || '').getFullYear()
    const currentYear = new Date().getFullYear()
    return currentYear - year <= 3
  }).length
  const citations = publications.reduce((sum, p) => sum + (p.citation_count || 0), 0)

  const firstAuthorCount = publications.filter((p) =>
    p.author_positions?.includes('first')
  ).length

  // Calculate h-index (simplified)
  const citationCounts = publications
    .map((p) => p.citation_count || 0)
    .sort((a, b) => b - a)
  let hIndex = 0
  for (let i = 0; i < citationCounts.length; i++) {
    if (citationCounts[i] >= i + 1) {
      hIndex = i + 1
    }
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-5 gap-3">
        <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4 text-center">
          <div className="text-[24px] font-bold text-[#e8e8f0]">
            {totalPublications}
          </div>
          <div className="text-[12px] text-[#a8a8c0] mt-2">Total</div>
        </div>

        <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4 text-center">
          <div className="text-[24px] font-bold text-[#e8e8f0]">
            {firstAuthorCount}
          </div>
          <div className="text-[12px] text-[#a8a8c0] mt-2">First Author</div>
        </div>

        <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4 text-center">
          <div className="text-[24px] font-bold text-[#e8e8f0]">
            {lastThreeYears}
          </div>
          <div className="text-[12px] text-[#a8a8c0] mt-2">Last 3 Years</div>
        </div>

        <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4 text-center">
          <div className="text-[24px] font-bold text-[#e8e8f0]">
            {citations}
          </div>
          <div className="text-[12px] text-[#a8a8c0] mt-2">Citations</div>
        </div>

        <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4 text-center">
          <div className="text-[24px] font-bold text-[#e8e8f0]">
            {hIndex}
          </div>
          <div className="text-[12px] text-[#a8a8c0] mt-2">h-index</div>
        </div>
      </div>

      {/* Publications List */}
      <CompletableSection
        sectionId="pub-publications"
        title="Publications"
        isComplete={isComplete('pub-publications')}
        completedByName={getInfo('pub-publications')?.completed_by_name}
        completedAt={getInfo('pub-publications')?.completed_at}
        onToggle={() => toggle('pub-publications')}
      >
        {publications.length > 0 ? (
          <div className="space-y-3">
            {publications.map((pub, idx) => (
              <div
                key={idx}
                className="
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg p-3
                "
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[13px] font-medium text-[#e8e8f0] line-clamp-2">
                      {pub.title}
                    </h4>
                    <div className="text-[11px] text-[#a8a8c0] mt-1">
                      {pub.authors?.slice(0, 2).join(', ')}
                      {pub.authors && pub.authors.length > 2 && ' et al.'}
                    </div>
                    <div className="text-[11px] text-[#8b8ba0] mt-1">
                      {pub.journal} ({new Date(pub.published_at || '').getFullYear()})
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-right">
                    <div className="text-[13px] font-semibold text-[#10B981]">
                      {pub.citation_count || 0}
                    </div>
                    <div className="text-[11px] text-[#a8a8c0]">citations</div>
                  </div>
                </div>

                {/* PubMed Link */}
                {pub.pubmed_id && (
                  <a
                    href={`https://pubmed.ncbi.nlm.nih.gov/${pub.pubmed_id}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
                      inline-block mt-2
                      text-[11px] text-[#3B82F6]
                      hover:text-[#2563EB] font-medium
                    "
                  >
                    View on PubMed →
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-[13px] text-[#a8a8c0] py-4">
            No publications recorded yet
          </div>
        )}
      </CompletableSection>

      {/* Related Events */}
      <CompletableSection
        sectionId="pub-conferences"
        title="Related Conferences & Events"
        isComplete={isComplete('pub-conferences')}
        completedByName={getInfo('pub-conferences')?.completed_by_name}
        completedAt={getInfo('pub-conferences')?.completed_at}
        onToggle={() => toggle('pub-conferences')}
      >
        {interactions && interactions.length > 0 ? (
          <div className="space-y-2">
            {interactions
              .filter((i) => i.interaction_type === 'conference')
              .map((event, idx) => (
                <div
                  key={idx}
                  className="
                    bg-[#12122a] border border-[#2a2a44]
                    rounded-lg p-3
                    text-[13px]
                  "
                >
                  <div className="text-[#e8e8f0] font-medium">
                    {event.name}
                  </div>
                  <div className="text-[12px] text-[#a8a8c0] mt-1">
                    {event.date && new Date(event.date).toLocaleDateString()}
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="text-[13px] text-[#a8a8c0]">
            No conference activity recorded
          </div>
        )}
      </CompletableSection>
    </div>
  )
}

export default PublicationsTab
